import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Snackbar, Alert, LinearProgress, Box } from '@mui/material';
import { bgColors, progressColors, textColors } from '../constants/colorConstant';

const SnackbarContext = createContext();

export const SnackbarProvider = ({ children }) => {
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success', // 'error' | 'success' | 'warning' | 'info'
    duration: 3000,
  });
  const [progress, setProgress] = useState(100);

  const showSnackbar = useCallback((message, severity = 'success', duration = 3000) => {
    setSnack({ open: true, message, severity, duration });
  }, []);

  useEffect(() => {
    if (!snack.open) {
      setProgress(100);
      return;
    }
    let start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const percentage = Math.max(100 - (elapsed / snack.duration) * 100, 0);
      setProgress(percentage);
      if (percentage > 0) requestAnimationFrame(animate);
    };
    animate();
  }, [snack.open, snack.duration]);

  const handleClose = (_, reason) => {
    if (reason !== 'clickaway') {
      setSnack(prev => ({ ...prev, open: false }));
    }
  };

 
  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}

      <Snackbar
        open={snack.open}
        autoHideDuration={snack.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          severity={snack.severity}
          sx={{
            width: '100%',
            alignItems: 'center',
            backgroundColor: bgColors[snack.severity] || '#f5f5f5',
            color: textColors[snack.severity] || '#333',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          }}
          variant="filled"
        >
          <Box display="flex" flexDirection="column">
            <span>{snack.message}</span>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={progressColors[snack.severity] || 'primary'}
              sx={{ height: 3, mt: 0.5, borderRadius: 1 }}
            />
          </Box>
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) throw new Error('useSnackbar must be used within a SnackbarProvider');
  return context.showSnackbar;
};
